import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, KeyRound, ArrowRight, Loader2, Building2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectionToken, setSelectionToken] = useState("");

  const requestOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { phone });
      setDevOtp(data.dev_otp || "");
      setStep("otp");
      toast.success(data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone, code });
      if (data.needs_tenant_choice) {
        setOptions(data.options);
        setSelectionToken(data.selection_token);
        setStep("tenant");
      } else {
        login(data.token, data.user, data.tenant);
        navigate("/");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const chooseTenant = async (tenant_id) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/select-tenant", { selection_token: selectionToken, tenant_id });
      login(data.token, data.user, data.tenant);
      navigate("/");
    } catch (e) { toast.error(apiError(e)); if (e?.response?.status === 401) setStep("phone"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-full grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#101D51] text-white p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#F83A84]/20 blur-3xl" />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full bg-[#0B7C5A]/20 blur-3xl" />
        <div className="flex items-center gap-2 relative">
          <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-9 h-9 rounded-lg bg-white p-1 object-contain" />
          <span className="font-bold text-xl">TradeDesk360</span>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-extrabold leading-tight">WhatsApp to invoice,<br />in one counter app.</h1>
          <p className="text-white/70 mt-4 max-w-md">Turn customer messages into enquiries, quotes, orders and invoices — built for small traders in India.</p>
          <div className="flex gap-6 mt-8 text-sm text-white/60">
            <div><div className="text-2xl font-bold text-white">GST</div>ready invoices</div>
            <div><div className="text-2xl font-bold text-white">PWA</div>works on the phone</div>
            <div><div className="text-2xl font-bold text-white">SLA</div>never miss a lead</div>
          </div>
        </div>
        <div className="text-white/40 text-xs relative">A Clotus Infotech product</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/tradedesk-logo.png" alt="TradeDesk360" className="h-9 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-[#101D51]">Sign in</h2>
          <p className="text-sm text-[#6B7189] mt-1 mb-6">
            {step === "phone" ? "Enter your registered mobile number."
              : step === "otp" ? `We sent a code to +91 ${phone}.`
              : "Almost there — pick your business."}
          </p>

          {step === "phone" ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#6B7189]">Mobile number</label>
                <div className="relative mt-1">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
                  <Input data-testid="login-phone-input" value={phone} inputMode="numeric"
                    onChange={(e) => setPhone(e.target.value)} placeholder="9876500001"
                    className="pl-9 h-11" onKeyDown={(e) => e.key === "Enter" && requestOtp()} />
                </div>
              </div>
              <Button data-testid="login-request-otp-btn" onClick={requestOtp} disabled={loading || phone.length < 10}
                className="w-full h-11 bg-[#101D51] hover:bg-[#080852]">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send OTP <ArrowRight className="w-4 h-4" /></>}
              </Button>
              <p className="text-sm text-center text-[#6B7189]">
                New to TradeDesk? <Link to="/signup" className="font-semibold text-[#F83A84] hover:underline" data-testid="login-to-signup">Start a free 15-day trial</Link>
              </p>
              <p className="text-[11px] text-center text-[#9AA0B4] pt-1">
                By continuing you agree to our <Link to="/terms" className="underline hover:text-[#101D51]" data-testid="login-terms-link">Terms</Link> &amp; <Link to="/privacy" className="underline hover:text-[#101D51]" data-testid="login-privacy-link">Privacy Policy</Link>.
              </p>
            </div>
          ) : step === "otp" ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#6B7189]">One-time password</label>
                <div className="relative mt-1">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
                  <Input data-testid="login-otp-input" value={code} inputMode="numeric" maxLength={6}
                    onChange={(e) => setCode(e.target.value)} placeholder="123456"
                    className="pl-9 h-11 tracking-[0.4em] font-semibold" onKeyDown={(e) => e.key === "Enter" && verify()} />
                </div>
              </div>
              {devOtp && (
                <div className="text-xs bg-[#F5F6FA] border border-[#E6E8F0] rounded-lg px-3 py-2 text-[#6B7189]">
                  Demo mode — your OTP is <span className="font-bold text-[#101D51]">{devOtp}</span>
                </div>
              )}
              <Button data-testid="login-verify-btn" onClick={verify} disabled={loading || code.length < 4}
                className="w-full h-11 bg-[#0B7C5A] hover:bg-[#09664a]">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
              </Button>
              <button onClick={() => setStep("phone")} className="text-sm text-[#6B7189] hover:text-[#101D51] w-full text-center">
                Change number
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="tenant-choice">
              <p className="text-sm text-[#6B7189]">You're an active user in more than one business. Choose where to sign in:</p>
              {options.map((o) => (
                <button key={o.tenant_id} onClick={() => chooseTenant(o.tenant_id)} disabled={loading}
                  data-testid={`tenant-choice-${o.tenant_id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E6E8F0] hover:border-[#101D51] hover:bg-[#F5F6FA] text-left transition-colors disabled:opacity-60">
                  <span className="w-9 h-9 rounded-lg bg-[#101D51] text-white flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#101D51] truncate">{o.tenant_name}</span>
                    <span className="block text-xs text-[#6B7189]">Role · {o.role}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-[#6B7189]" />
                </button>
              ))}
              <button onClick={() => setStep("phone")} className="text-sm text-[#6B7189] hover:text-[#101D51] w-full text-center">Start over</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
